import { useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Link } from 'react-router-dom';
import { logEvent } from '@amplitude/analytics-browser';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Layout from '../../../componentsv2/Layout';
import Typography from '../../../componentsv2/Typography';
import Section from '../../../componentsv2/Section';
import { Tabs, Tab } from '../../../componentsv2/Tabs';
import Button from '../../../componentsv2/Button';
import SimpleBackdrop from '../../../components/Backdrop';
import Modal from '../../../componentsv2/Modal';
import { TextField } from '../../../componentsv2/Field';
import Form from '../../../componentsv2/Form';
import ErrorMessage from '../../../components/ErrorMessage';
import { changePassword } from '../../../services/companyUsers';
import { getLLC } from '../../../services/llc';
import { updateCompanyUser, updateCompany, useAppDispatch, useAppSelector } from '../../../store';
import { useSnackbar } from '../../../context';
import { useTranslation } from '../../../hooks/i18n';
import { myAccountEvents } from '../../../constants/events';
import { CountryKeys, Nullable } from '../../../types';
import { APILLC } from '../../../types/llc';
import { COLORS } from '../../../themev2';
import NotificationsSettings from '../../UserManagement/NotificationsSettings';
import UserInvites from '../../UserManagement/UserInvites';
import CompanyTab from './CompanyTab';
import SecurityTab from './SecurityTab';
import { useUserLocation } from '../../../hooks/useUserLocation';
import LanguageSelector from '../../../componentsv2/LanguageSelector';
import { Language } from '../../../services/types';
import TransferApprovals from './TransferApprovals';

enum TabValues {
  Account = 'account',
  Company = 'company',
  Users = 'users',
  Security = 'security',
  Notifications = 'notifications',
  TransferApprovals = 'transferApprovals',
}

const MyAccount = () => {
  const { t } = useTranslation(['myAccount', 'user', 'client']);
  const {
    companyUser: { status, companyUser },
  } = useAppSelector((state) => state);
  const companyId = companyUser?.selectedCompany.id;
  const dispatch = useAppDispatch();
  const [showPersonalInfoModal, setShowPersonalInfoModal] = useState(false);
  const [showCompanyInfoModal, setShowCompanyInfoModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [isLoadingPassword, setIsLoadingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<Nullable<any>>(null);
  const [currentTab, setCurrentTab] = useState(TabValues.Account);
  const [isLoading, setIsLoading] = useState(false);
  const [llc, setLlc] = useState<Nullable<APILLC>>(null);
  const { showSnackbar } = useSnackbar();
  const { handleChangeLocation, language } = useUserLocation();

  useEffect(() => {
    if (!companyId) return;
    setIsLoading(true);
    getLLC(companyId)
      .then((result) => {
        if (result.length > 0) {
          setLlc(result[0]);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [companyId]);

  useEffect(() => {
    logEvent(myAccountEvents.VIEW_PAGE);
  }, []);

  if (!companyUser) {
    return <SimpleBackdrop isLoading hasOpaqueBackground />;
  }

  const selectedCompany = companyUser.selectedCompany;
  const companyUserRole = companyUser?.companyUserRoles.find(
    (companyUserRole) => companyUserRole.company.id === selectedCompany.id
  );
  const isCompanyAdministrator =
    companyUserRole?.role === 'administrator' || companyUserRole?.role === 'owner';

  return (
    <Layout>
      <Typography variant="h5" sx={{ marginBottom: '1em' }}>
        {t('title')}
      </Typography>

      <Grid container>
        <Grid item xs={12}>
          <Tabs
            value={currentTab}
            onChange={(e, newValue) => {
              setCurrentTab(newValue);
            }}
            variant="scrollable"
          >
            <Tab label={t('accountTab')} value={TabValues.Account} />
            <Tab label={t('companyTab')} value={TabValues.Company} />
            <Tab label={t('usersTab')} value={TabValues.Users} />
            <Tab label={t('securityTab')} value={TabValues.Security} />
            <Tab label={t('notificationsTab')} value={TabValues.Notifications} />
            <Tab label={t('transferApprovalsTab')} value={TabValues.TransferApprovals} />
          </Tabs>
        </Grid>
        {currentTab === TabValues.Account && (
          <>
            <Grid item xs={12}>
              <Section>
                <Grid container alignItems="center">
                  <Grid item xs={8} md={11}>
                    <Typography fontWeight="600" sx={{ fontSize: '18px' }}>
                      {t('personalInfo.title')}
                    </Typography>
                  </Grid>
                  <Grid item xs={4} md={1}>
                    <Button
                      color="secondary"
                      size="small"
                      onClick={() => {
                        setShowPersonalInfoModal(true);
                      }}
                    >
                      {t('edit')}
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography sx={{ color: COLORS.gray[1100] }}>{t('personalInfo.fullName')}</Typography>
                    <Typography fontWeight="600">{companyUser.name} </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography sx={{ color: COLORS.gray[1100] }}>{t('personalInfo.email')}</Typography>
                    <Typography fontWeight="600" sx={{ wordBreak: 'break-word' }}>
                      {companyUser.user.email}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography sx={{ color: COLORS.gray[1100] }}>{t('personalInfo.phoneNumber')}</Typography>
                    <Typography fontWeight="600">{companyUser.contactPhone} </Typography>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <Typography sx={{ color: COLORS.gray[1100] }}>{t('personalInfo.password')}</Typography>
                    <Link
                      to="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowPasswordModal(true);
                      }}
                    >
                      {t('personalInfo.changePassword')}
                    </Link>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <Typography sx={{ color: COLORS.gray[1100] }}>{t('common:language')}</Typography>
                    <LanguageSelector
                      currentLanguage={(language as Language) || Language.ES}
                      onChange={(lang) => handleChangeLocation(lang as CountryKeys)}
                    />
                  </Grid>
                </Grid>
              </Section>
            </Grid>
            <Modal
              isOpen={showPersonalInfoModal}
              handleClose={() => {
                setShowPersonalInfoModal(false);
              }}
              isAutoHeight
            >
              <Typography
                variant="h5"
                sx={{
                  background: COLORS.secondary.greenGradient,
                  backgroundClip: 'text',
                  '-webkit-background-clip': 'text',
                  color: 'transparent',
                }}
              >
                {t('personalInfo.title')}
              </Typography>
              <Divider sx={{ marginY: '1em' }} />
              <Formik
                initialValues={{
                  firstName: companyUser.user.firstName || '',
                  lastName: companyUser.user.lastName || '',
                  email: companyUser.user.email || '',
                  contactPhone: companyUser.contactPhone || '',
                }}
                onSubmit={(values) => {
                  const { firstName, lastName, ...restOfFields } = values;
                  const mappedFields = {
                    user: { firstName, lastName },
                    ...restOfFields,
                  };
                  dispatch(updateCompanyUser(mappedFields)).then(() => {
                    logEvent(myAccountEvents.EDIT_PERSONAL_INFO, values);
                    showSnackbar(t('personalInfo.success'), 'success');
                    setShowPersonalInfoModal(false);
                  });
                }}
              >
                {({ dirty }) => (
                  <Form>
                    <TextField name="firstName" label={t('user:signup.firstName.label')} />
                    <TextField name="lastName" label={t('user:signup.lastName.label')} />
                    <TextField disabled name="email" label={t('personalInfo.email')} />
                    <TextField name="contactPhone" label={t('personalInfo.phoneNumber')} />
                    <Box sx={{ marginY: '2em' }}>
                      <Button
                        color="secondary"
                        fullWidth
                        isLoading={status === 'loading'}
                        isSubmit
                        disabled={!dirty}
                      >
                        {t('saveChanges')}
                      </Button>
                    </Box>
                  </Form>
                )}
              </Formik>
            </Modal>
            <Modal
              isOpen={showCompanyInfoModal}
              handleClose={() => {
                setShowCompanyInfoModal(false);
              }}
              isAutoHeight
            >
              <Typography
                variant="h5"
                sx={{
                  background: COLORS.secondary.greenGradient,
                  backgroundClip: 'text',
                  '-webkit-background-clip': 'text',
                  color: 'transparent',
                }}
              >
                {t('companyInfo.title')}
              </Typography>
              <Divider sx={{ marginY: '1em' }} />
              <Formik
                initialValues={{
                  fullName: selectedCompany.name || '',
                  taxId: selectedCompany.taxId || '',
                  addressStreetOne: selectedCompany.addressStreetOne || '',
                  addressZipCode: selectedCompany.addressZipCode || '',
                  addressCity: selectedCompany.addressCity || '',
                  addressState: selectedCompany.addressState || '',
                }}
                onSubmit={(values) => {
                  dispatch(
                    updateCompany({
                      companyId: selectedCompany.id,
                      payload: values,
                    })
                  ).then(() => {
                    logEvent(myAccountEvents.EDIT_COMPANY_INFO, values);
                    showSnackbar(t('companyInfo.success'), 'success');
                    setShowCompanyInfoModal(false);
                  });
                }}
              >
                {({ dirty }) => (
                  <Form>
                    <TextField disabled name="fullName" label={t('companyInfo.fullName')} />
                    <Box sx={{ display: 'flex', gap: '1em' }}>
                      <TextField name="addressStreetOne" label={t('companyInfo.addressStreetOne')} />
                      <TextField name="addressCity" label={t('companyInfo.addressCity')} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: '1em' }}>
                      <TextField name="addressState" label={t('companyInfo.addressState')} />
                      <TextField name="addressZipCode" label={t('companyInfo.addressZipCode')} />
                    </Box>
                    <TextField disabled name="taxId" label={t('companyInfo.taxId')} />
                    <Box sx={{ marginY: '2em' }}>
                      <Button
                        color="secondary"
                        fullWidth
                        isLoading={status === 'loading'}
                        isSubmit
                        disabled={!dirty}
                      >
                        {t('saveChanges')}
                      </Button>
                    </Box>
                  </Form>
                )}
              </Formik>
            </Modal>
            <Modal
              isAutoHeight
              isOpen={showPasswordModal}
              handleClose={() => {
                setPasswordError(null);
                setShowPasswordModal(false);
              }}
            >
              <Grid container>
                <Grid item xs={12}>
                  <Typography
                    variant="h5"
                    sx={{
                      background: COLORS.secondary.greenGradient,
                      backgroundClip: 'text',
                      '-webkit-background-clip': 'text',
                      color: 'transparent',
                    }}
                  >
                    {t('changePassword.title')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Formik
                    initialValues={{ oldPassword: '', newPassword: '' }}
                    validationSchema={Yup.object().shape({
                      oldPassword: Yup.string().required(t('common:requiredField')),
                      newPassword: Yup.string().required(t('common:requiredField')),
                    })}
                    onSubmit={(values) => {
                      setIsLoadingPassword(true);
                      setPasswordError(null);
                      changePassword(values)
                        .then(() => {
                          logEvent(myAccountEvents.CHANGE_PASSWORD);
                          showSnackbar(t('changePassword.passwordSuccess'), 'success');
                          setShowPasswordModal(false);
                        })
                        .catch((error) => {
                          setPasswordError(error);
                        })
                        .finally(() => {
                          setIsLoadingPassword(false);
                        });
                    }}
                  >
                    <Form>
                      <TextField type="password" name="oldPassword" label={t('changePassword.oldPassword')} />
                      <TextField type="password" name="newPassword" label={t('changePassword.newPassword')} />
                      {passwordError && <ErrorMessage errorMessage={t('changePassword.passwordError')} />}
                      <Box sx={{ marginY: '2em' }}>
                        <Button color="secondary" fullWidth isLoading={isLoadingPassword} isSubmit>
                          {t('saveChanges')}
                        </Button>
                      </Box>
                    </Form>
                  </Formik>
                </Grid>
              </Grid>
            </Modal>
          </>
        )}
        {currentTab === TabValues.Company && (
          <Grid item xs={12}>
            <CompanyTab company={selectedCompany} t={t} />
          </Grid>
        )}
        {currentTab === TabValues.Users && (
          <Grid item xs={12}>
            <UserInvites
              company={companyUser.selectedCompany!}
              currentCompanyUser={companyUser!}
              isCompanyAdministrator={isCompanyAdministrator}
            />
          </Grid>
        )}
        {currentTab === TabValues.Security && (
          <Grid item xs={12}>
            <SecurityTab t={t} />
          </Grid>
        )}

        {currentTab === TabValues.Notifications && (
          <Grid item xs={12}>
            <NotificationsSettings />
          </Grid>
        )}
        {currentTab === TabValues.TransferApprovals && (
          <>
            <Grid item xs={12}>
              <TransferApprovals companyId={selectedCompany.id} />
            </Grid>
          </>
        )}
      </Grid>
    </Layout>
  );
};

export default MyAccount;
